import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCrews } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { EmptyState } from "@/components/v2/empty-state";
import { ListErrorState } from "@/components/v2/list-error-state";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { FilterToolbar } from "@/components/v2/filter-toolbar";
import { SearchHint } from "@/components/v2/search-hint";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";

interface CrewListItem {
  id: string;
  name: string | null;
  status: string | null;
  worker_count: number | null;
  started_on: string | null;
  home_base_location: string | null;
  leader_first_name: string | null;
  leader_last_name: string | null;
  member_count: number;
}

function leaderName(first: string | null, last: string | null): string | null {
  const full = [first, last].filter(Boolean).join(" ").trim();
  return full || null;
}

function leaderInitials(first: string | null, last: string | null): string {
  const f = (first ?? "").charAt(0).toUpperCase();
  const l = (last ?? "").charAt(0).toUpperCase();
  return f + l || "—";
}

function size(crew: CrewListItem): number {
  return crew.worker_count ?? crew.member_count ?? 0;
}

// Cards are non-interactive until the crew detail route lands (ADV-785).
// Keeping them as plain Card elements avoids the broken /crews/:id navigation
// flagged during review.
function CrewCard({ crew }: { crew: CrewListItem }) {
  const leader = leaderName(crew.leader_first_name, crew.leader_last_name);
  const sizeLabel = `${size(crew)} ${size(crew) === 1 ? "member" : "members"}`;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="typo-body font-medium text-[color:var(--color-text-hi)] leading-tight">
          {crew.name ?? "Unnamed crew"}
        </span>
        {crew.status && <StatusBadge status={crew.status} />}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="text-[10px] font-medium">
            {leaderInitials(crew.leader_first_name, crew.leader_last_name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="typo-body text-[color:var(--color-text-hi)] truncate">
            {leader ?? "No leader assigned"}
          </p>
          <p className="typo-label text-[color:var(--color-text-lo)] normal-case tracking-normal">
            Crew Leader
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 typo-label text-[color:var(--color-text-md)] normal-case tracking-normal">
        <span>{sizeLabel}</span>
        {crew.home_base_location && (
          <>
            <span aria-hidden>·</span>
            <span className="truncate">{crew.home_base_location}</span>
          </>
        )}
      </div>
    </Card>
  );
}

function CrewCardSkeleton() {
  return (
    <Card className="p-4">
      <div className="h-4 w-32 rounded bg-[color:var(--color-border)]/60" />
      <div className="mt-3 flex items-center gap-2">
        <div className="size-7 rounded-full bg-[color:var(--color-border)]/60" />
        <div className="flex-1">
          <div className="h-3 w-24 rounded bg-[color:var(--color-border)]/60" />
          <div className="mt-1.5 h-2 w-16 rounded bg-[color:var(--color-border)]/40" />
        </div>
      </div>
      <div className="mt-3 h-3 w-20 rounded bg-[color:var(--color-border)]/60" />
    </Card>
  );
}

const VIEWS = LIST_PAGE_VIEWS.crews;

export default function V2CrewsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<string>(VIEWS.defaultValue);
  const { data, isLoading, isError, refetch } = useCrews({ limit: 50 });

  const crews = (data?.crews ?? []) as CrewListItem[];
  const total = data?.total ?? 0;

  // TODO(ADV-787-followup): Active crews / On site / Available need a DB-side
  // aggregate. Until then we render `—`.
  const aggregateItems = [
    { label: "Showing", value: `${crews.length} of ${total}` },
    { label: "Active crews", value: "—" },
    { label: "On site", value: "—" },
    { label: "Available", value: "—" },
  ];

  return (
    <ListPage
      title="Crews"
      actions={
        <>
          <SearchHint />
          <Button onClick={() => navigate("/crews/new")}>Create crew</Button>
        </>
      }
      tabs={
        <ViewSwitcher
          value={view}
          onValueChange={setView}
          options={[...VIEWS.options]}
          aria-label="Crews view"
        />
      }
      filters={<FilterToolbar entity="crews" />}
      aggregateStrip={
        view === "crews" ? (
          <AggregateStrip
            items={aggregateItems}
            sort={{ label: "Name", direction: "asc" }}
          />
        ) : undefined
      }
    >
      {view === "crews" &&
        (isLoading ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <CrewCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <ListErrorState entityLabel="crews" onRetry={() => refetch()} />
        ) : crews.length === 0 ? (
          <EmptyState
            title="No crews yet"
            description="Crews appear here once you create one."
            action={{
              label: "Create crew",
              onClick: () => navigate("/crews/new"),
            }}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {crews.map((crew) => (
              <CrewCard key={crew.id} crew={crew} />
            ))}
          </div>
        ))}
      {view === "table" && (
        <EmptyState
          title="Table view coming soon"
          description="A row-per-crew DataTable view will follow once the Crews detail route lands."
        />
      )}
    </ListPage>
  );
}
